import { useQuery } from '@tanstack/react-query';
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { useErrorHandler } from '@/utils/errorHandling/centralizedErrorHandler';
import { Database } from '@/types/supabase';

type TableName = keyof Database['public']['Tables'] | 'sales' | 'sale_items';

interface Entity {
  id: string;
}

interface UseEntityListProps<T extends Entity, TTableName extends TableName> {
  tableName: TTableName;
  queryKey: (string | Record<string, unknown> | undefined)[];
  dbEntityToEntityMapper: (dbEntity: unknown) => T; // Unknown for joined tables; mapper is responsible for narrowing
  filters?: Record<string, unknown>;
  enabled?: boolean;
  selectQuery?: string;
}

export const useEntityList = <T extends Entity, TTableName extends TableName>({
  tableName,
  queryKey,
  dbEntityToEntityMapper,
  filters,
  enabled = true,
  selectQuery = '*',
}: UseEntityListProps<T, TTableName>) => {
  const { handleError } = useErrorHandler();

  const fetchEntities = async () => {
    console.log(`[useEntityList] Fetching from table: ${tableName}...`);
    console.log(`[useEntityList] Filters:`, filters);

    try {
      let query = supabaseWorker.from(tableName as unknown as string).select(selectQuery);
      // Debug: log the select query to verify joins requested
      console.log(`[useEntityList] Select for ${tableName}:`, selectQuery);

      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            console.log(`[useEntityList] Adding filter: ${key} = ${value}`);
            if (typeof key === 'string' && typeof (query as unknown as { eq?: unknown }).eq === 'function') {
              query = (query as unknown as { eq: (k: string, v: unknown) => typeof query }).eq(key, value as unknown);
            }
          }
        });
      }

      // Default sorting
      query = query.order('created_at', { ascending: false });

      // КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ: Упрощенный запрос без Promise.race
      console.log(`[useEntityList] Executing query for ${tableName}...`);
      const { data, error } = await query;
      const dataLen = Array.isArray(data) ? data.length : 0;
      const errorMsg = ((): string | undefined => {
        if (!error) return undefined;
        if (typeof error === 'object' && 'message' in error) {
          const m = (error as { message?: unknown }).message;
          return typeof m === 'string' ? m : undefined;
        }
        return undefined;
      })();
      console.log(`[useEntityList] Query completed for ${tableName}:`, { 
        dataLength: dataLen, 
        hasError: !!error,
        errorMessage: errorMsg 
      });
      
      if (error) {
        console.error(`[useEntityList] Error fetching ${tableName}:`, error);
        throw error;
      }

      if (!data) {
        console.log(`[useEntityList] No data returned for ${tableName}, returning empty array`);
        return [];
      }

      console.log(`[useEntityList] Successfully fetched ${Array.isArray(data) ? data.length : 0} items from ${tableName}`);
      // Debug: sample the first raw row keys to verify nested relations presence
      try {
        if (data[0]) {
          const firstRow = data[0] as Record<string, unknown>;
          console.log(`[useEntityList] Sample row keys for ${tableName}:`, Object.keys(firstRow));
        }
      } catch (e) {
        // no-op
      }
      const rows = data as unknown[];
      const mappedResults = rows.map(dbEntityToEntityMapper);
      console.log(`[useEntityList] Mapped ${mappedResults.length} results for ${tableName}`);
      return mappedResults;
      
    } catch (err) {
      console.error(`[useEntityList] Fetch failed for ${tableName}:`, err);
      // КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ: Возвращаем пустой массив вместо выброса ошибки
      // Это позволит UI показать empty state вместо бесконечной загрузки
      return [];
    }
  };

  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching
  } = useQuery<T[], Error>({ 
    queryKey, 
    queryFn: fetchEntities, 
    enabled,
    // КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ: Упрощенные настройки для быстрой загрузки
    staleTime: 60000, // 60 сек: снижает частые refetch при неизменных фильтрах
    gcTime: 300000, // 5 минут: современная замена cacheTime в React Query v5
    retry: 1, // Только 1 повтор при ошибке
    retryDelay: 500, // 0.5 секунды между повторами
    refetchOnWindowFocus: false, // Не перезапрашивать при фокусе окна
    refetchOnMount: true, // Перезапрашивать при монтировании
    timeout: 8000, // 8 секунд таймаут для React Query
    onError: (err) => {
      console.error(`[useEntityList] Query error for ${tableName}:`, err);
      handleError(err, { operation: `fetch ${tableName}` });
    }
  });

  return {
    data: data || [],
    isLoading,
    isFetching,
    error,
    refetch,
  };
};
